import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { Download, Smartphone, Check, Share, Plus, MoreVertical } from "lucide-react";
import { Link } from "react-router-dom";

const Install = () => {
  const { isInstallable, isInstalled, promptInstall, isIOS, isAndroid } = usePWAInstall();

  const handleInstall = async () => {
    await promptInstall();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-20 h-20 mx-auto bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
            <Smartphone className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Install RentFlow</h1>
          <p className="text-muted-foreground">
            Get the full app experience on your device
          </p>
        </div>

        {/* Already Installed */}
        {isInstalled && (
          <Card className="border-green-500/50 bg-green-500/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="font-medium text-foreground">App Installed!</p>
                  <p className="text-sm text-muted-foreground">
                    RentFlow is ready to use on your device
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Install Button (Android/Desktop with prompt) */}
        {isInstallable && !isInstalled && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Quick Install
              </CardTitle>
              <CardDescription>
                Install RentFlow with one tap
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleInstall} className="w-full" size="lg">
                <Download className="w-4 h-4 mr-2" />
                Install RentFlow
              </Button>
            </CardContent>
          </Card>
        )}

        {/* iOS Instructions */}
        {isIOS && !isInstalled && (
          <Card>
            <CardHeader>
              <CardTitle>Install on iPhone/iPad</CardTitle>
              <CardDescription>
                Follow these steps to add RentFlow to your home screen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-primary">1</span>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-foreground">Tap the Share button</p>
                  <Share className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-primary">2</span>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-foreground">Scroll down and tap "Add to Home Screen"</p>
                  <Plus className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-primary">3</span>
                </div>
                <p className="text-foreground">Tap "Add" to install RentFlow</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Android Manual Instructions (fallback) */}
        {isAndroid && !isInstallable && !isInstalled && (
          <Card>
            <CardHeader>
              <CardTitle>Install on Android</CardTitle>
              <CardDescription>
                Add RentFlow to your home screen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-primary">1</span>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-foreground">Tap the menu button</p>
                  <MoreVertical className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-primary">2</span>
                </div>
                <p className="text-foreground">Tap "Install app" or "Add to Home screen"</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-primary">3</span>
                </div>
                <p className="text-foreground">Confirm installation</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Benefits */}
        <Card>
          <CardHeader>
            <CardTitle>Why Install?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-foreground">Works offline - view cached data anytime</span>
            </div>
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-foreground">Faster loading - app launches instantly</span>
            </div>
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-foreground">Full-screen experience - no browser bars</span>
            </div>
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-foreground">Home screen access - launch like any app</span>
            </div>
          </CardContent>
        </Card>

        {/* Back to App */}
        <div className="text-center">
          <Link to="/" className="text-primary hover:underline">
            ← Back to RentFlow
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Install;
